import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useKeysCountQuery } from "@/query/keysCountGet"
import { usePromotions } from "@/query/promotionQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import {
  addServiceItem,
  removeServiceItem,
  setKeysUsed,
  updateServiceCancelled,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import {
  BookingServiceItem,
  DiscountAppliedUnique,
  KeysPointsUsed,
} from "@okeys/shared-utils"
import { Minus, Plus, Trash } from "lucide-react"
import React, { useMemo } from "react"

// Type local pour la gestion frontend avec count et cancelledCount
export type ServiceWithCount = {
  serviceId: string
  name: string
  price: number
  count: number
  fidelityPoints: number
  fidelityExclusion: boolean
  fromBooking: boolean
  discountApplied: DiscountAppliedUnique<"booking"> | null
  cancelledCount: number
  totalSubPrice: number
  totalEffectivePrice: number
  totalDiscount: number
  promotionIds: Set<string>
}

type ServiceDisplayProps = {
  service: ServiceWithCount
  onToggleService: (serviceId: string) => void
  onRemoveService: (serviceId: string) => void
  onAddService: (serviceId: string) => void
  onUpdateCancelledCount: (serviceId: string, cancelledCount: number) => void
}

const ServiceDisplay: React.FC<ServiceDisplayProps> = ({
  service,
  onToggleService,
  onRemoveService,
  onAddService,
  onUpdateCancelledCount,
}) => {
  const dispatch = useAppDispatch()
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)
  const { services } = useServicesQuery()
  const { promotions } = usePromotions()
  const clientSelected = collectionInfo?.selectedClientId
  const { keysCount } = useKeysCountQuery({ clientId: clientSelected })
  const isNonPerformed = service.cancelledCount > 0
  const isPromoted = service.promotionIds.size > 0
  const isKeysPointsPromotion = service.discountApplied?.type === "keys_points"

  const serviceKeysUsedEligible = collectionInfo?.serviceWithPromo.filter(
    (servicePromo) =>
      (servicePromo.discountApplied === null ||
        servicePromo.discountApplied?.type === "keys_points") &&
      servicePromo.serviceId === service.serviceId
  )

  const listItemSelectKeysUsed =
    serviceKeysUsedEligible
      ?.map((servicePromo, index) => {
        const serviceTable = services?.find(
          (serv) => serv.id === servicePromo.serviceId
        )
        const keyAsk = serviceTable?.fidelityPoints
        if (keysCount === null) {
          return undefined
        }
        if (!keyAsk) {
          return undefined
        }
        const keysUsedForOtherService = collectionInfo?.serviceWithPromo
          .filter((key) => key.serviceId !== servicePromo.serviceId)
          .filter(
            (key) =>
              key.discountApplied !== null &&
              key.discountApplied.type === "keys_points"
          )
          .reduce(
            (acc, key) =>
              acc + (key.discountApplied as KeysPointsUsed<"booking">).keysUsed,
            0
          )
        if (
          keysCount &&
          keysCount.keysNumber <
            (index + 1) * keyAsk + (keysUsedForOtherService || 0)
        ) {
          return undefined
        }
        const discount =
          serviceTable?.price * (serviceTable.fidelityPercentageDiscount / 100)
        const value = `utiliser ${(index + 1) * keyAsk} keys pour - ${(
          discount *
          (index + 1)
        ).toFixed(2)}€ ( ${serviceTable.fidelityPercentageDiscount}% )`
        return {
          value,
          id: `${servicePromo.serviceId}-${index + 1}`,
          numberItemWithKeysUsed: index + 1,
        }
      })
      .filter((item) => item !== undefined) || []

  if (
    listItemSelectKeysUsed.length > 0 ||
    collectionInfo?.keysUsed.find((key) => key.serviceId === service.serviceId)
  ) {
    listItemSelectKeysUsed.unshift({
      value: "ne pas utiliser de clés",
      id: `${service.serviceId}-0`,
      numberItemWithKeysUsed: 0,
    })
  }

  const handleChangeSelectKeysUsed = (value: string) => {
    const valueSplited = value.split("-")
    const numberItemWithKeysUsed = valueSplited[valueSplited.length - 1] // Dernier élément
    const serviceIdParts = valueSplited.slice(0, -1) // Tous les éléments sauf le dernier
    const serviceId = serviceIdParts.join("-") // Rejoindre avec "-" au cas où l'ID contient des tirets
    dispatch(
      setKeysUsed({
        serviceId,
        numberItemWithKeysUsed: parseInt(numberItemWithKeysUsed),
        promotions: promotions || [],
        availableServices: services || [],
      })
    )
  }

  return (
    <Card
      className={`transition-all duration-200 py-4 ${
        isNonPerformed
          ? "border-red-200 bg-red-50 opacity-75"
          : isPromoted
          ? isKeysPointsPromotion
            ? "border-primary bg-primary/10"
            : "border-blue-800 bg-blue-500/10"
          : "border-border bg-card"
      }`}
    >
      <CardContent className="px-4">
        <div className="flex items-center justify-between w-full">
          <div className="w-full flex flex-col gap-2">
            <div
              className={`${
                service.cancelledCount === service.count ? "line-through" : ""
              } font-medium text-card-foreground flex items-center gap-2 w-full`}
            >
              {service.name}
              <Badge
                variant="secondary"
                className="bg-white/20 text-card-foreground text-xs"
              >
                x{service.count}
              </Badge>
              {service.cancelledCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-red-100 text-red-800 text-xs"
                >
                  {service.cancelledCount} non effectué
                  {service.cancelledCount > 1 ? "s" : ""}
                </Badge>
              )}
              {isPromoted && (
                <Badge
                  variant="secondary"
                  className={
                    isKeysPointsPromotion
                      ? "bg-primary/30 text-primary-foreground text-xs"
                      : "bg-blue-500/30 text-card-foreground text-xs"
                  }
                >
                  Remise -{service.totalDiscount.toFixed(2)}€
                </Badge>
              )}
            </div>
            <div
              className={`${
                service.cancelledCount === service.count ? "line-through" : ""
              } flex items-center gap-1 text-green-600 font-semibold w-full justify-between`}
            >
              {isPromoted ? (
                <div className="w-full flex items-center gap-1">
                  <p className="line-through">
                    {(service.price * service.count).toFixed(2)} €
                  </p>
                  <p className="text-blue-600">
                    {service.totalEffectivePrice.toFixed(2)}€
                  </p>
                </div>
              ) : (
                <p>{service.totalEffectivePrice.toFixed(2)}€</p>
              )}

              <div className="flex gap-2 items-center w-full justify-end">
                {service.fromBooking ? (
                  service.count > 1 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Non effectués :
                      </span>
                      <Select
                        value={service.cancelledCount.toString()}
                        onValueChange={(value) =>
                          onUpdateCancelledCount(
                            service.serviceId,
                            parseInt(value)
                          )
                        }
                      >
                        <SelectTrigger className="w-20 h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: service.count + 1 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {i}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <Button
                      variant={isNonPerformed ? "default" : "outline"}
                      size="sm"
                      onClick={() => onToggleService(service.serviceId)}
                      className={
                        isNonPerformed
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "border-red-200 text-red-600 hover:bg-red-50"
                      }
                    >
                      {isNonPerformed ? (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Remettre
                        </>
                      ) : (
                        <>
                          <Minus className="h-4 w-4 mr-1" />
                          Non effectué
                        </>
                      )}
                    </Button>
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    {service.count > 1 ? (
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => {
                          onRemoveService(service.serviceId)
                        }}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0 bg-white text-red-500 hover:text-red-400"
                        onClick={() => {
                          onRemoveService(service.serviceId)
                        }}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    )}
                    <div className="min-w-[2.5rem] text-card-foreground text-center font-medium text-sm px-2 py-1 border border-white/20 rounded bg-white/20">
                      {service.count}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => onAddService(service.serviceId)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {listItemSelectKeysUsed.length > 0 &&
              !service.fidelityExclusion && (
                <Select
                  value={
                    collectionInfo?.keysUsed.find(
                      (key) => key.serviceId === service.serviceId
                    )
                      ? `${service.serviceId}-${
                          collectionInfo.keysUsed.find(
                            (key) => key.serviceId === service.serviceId
                          )?.numberItemWithKeysUsed
                        }`
                      : `${service.serviceId}-0`
                  }
                  onValueChange={handleChangeSelectKeysUsed}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir le nombre de clés à utiliser" />
                  </SelectTrigger>
                  <SelectContent>
                    {listItemSelectKeysUsed?.map((item) => (
                      <SelectItem key={item?.id} value={item.id}>
                        {item?.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const ServicesList: React.FC = () => {
  const dispatch = useAppDispatch()
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)
  const { promotions, isLoadingPromotions, errorPromotions } = usePromotions()
  const { services, pendingServices, errorServices } = useServicesQuery()

  const formattedServices = useMemo(() => {
    if (!collectionInfo?.serviceWithPromo.length) return []

    const grouped: { [key: string]: ServiceWithCount } = {}

    collectionInfo.serviceWithPromo.forEach((service: BookingServiceItem) => {
      const key = service.serviceId
      const serviceTable = services?.find((s) => s.id === key)
      if (!grouped[key]) {
        grouped[key] = {
          serviceId: key,
          name: service.name,
          price: service.price,
          count: 0,
          fidelityPoints: serviceTable?.fidelityPoints || 0,
          fidelityExclusion: serviceTable?.fidelityExclusion || false,
          fromBooking: service.walkInAdded === false,
          discountApplied: service.discountApplied,
          cancelledCount: 0,
          totalSubPrice: 0,
          totalEffectivePrice: 0,
          totalDiscount: 0,
          promotionIds: new Set(),
        }
      }

      grouped[key].count++

      if (service.cancelled) {
        grouped[key].cancelledCount++
      } else {
        const oldPrice = service.price
        const newPrice = service.discountApplied
          ? service.discountApplied.priceEffect.newPrice
          : oldPrice
        const discount = oldPrice - newPrice

        grouped[key].totalSubPrice += oldPrice
        grouped[key].totalEffectivePrice += newPrice
        grouped[key].totalDiscount += discount

        if (service.discountApplied) {
          grouped[key].promotionIds.add(service.discountApplied.type)
        }
      }
    })

    return Object.values(grouped)
  }, [collectionInfo?.serviceWithPromo, services])

  if (
    isLoadingPromotions ||
    pendingServices ||
    errorPromotions ||
    errorServices
  ) {
    return <div>Chargement...</div>
  }

  if (errorPromotions || errorServices) {
    return <div>Erreur lors du chargement des données</div>
  }

  const onToggleService = (serviceId: string) => {
    console.log("onToggleService", serviceId)
  }

  const onRemoveService = (serviceId: string) => {
    const service = collectionInfo?.services.find(
      (s) => s.serviceId === serviceId
    )
    if (!service) return

    dispatch(
      removeServiceItem({
        serviceId,
        promotions: promotions || [],
        availableServices: services || [],
      })
    )
  }

  const onAddService = (serviceId: string) => {
    const service = collectionInfo?.serviceWithPromo.find(
      (s) => s.serviceId === serviceId
    )
    if (!service) return
    dispatch(
      addServiceItem({
        serviceItem: service,
        promotions: promotions || [],
        availableServices: services || [],
      })
    )
  }

  const onUpdateCancelledCount = (serviceId: string, cancelledCount: number) =>
    dispatch(
      updateServiceCancelled({
        serviceIds: serviceId,
        count: cancelledCount,
      })
    )

  return (
    <div
      style={{ maxHeight: "calc(95vh - 240px)" }}
      className="border-1 border-border bg-card rounded-lg p-4 flex flex-col"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-card-foreground">
          Prestations du rendez-vous
        </h3>
        <Badge
          variant="secondary"
          className="bg-primary text-primary-foreground"
        >
          {formattedServices.reduce((sum, service) => sum + service.count, 0)}{" "}
          prestation
          {formattedServices.reduce((sum, service) => sum + service.count, 0) >
          1
            ? "s"
            : ""}
        </Badge>
      </div>

      <div className="overflow-y-auto space-y-3 min-h-0 flex-1">
        {formattedServices.map((service) => (
          <ServiceDisplay
            key={service.serviceId}
            service={service}
            onToggleService={onToggleService}
            onRemoveService={onRemoveService}
            onAddService={onAddService}
            onUpdateCancelledCount={onUpdateCancelledCount}
          />
        ))}
      </div>
    </div>
  )
}
